/**
 * Bay Gating Validation Helper
 * 
 * Computes allowed bays for multi-job tickets using INTERSECTION logic.
 * Ensures technicians have compatible bay assignments before placement.
 * 
 * Used by: smart-assign, reassign-ticket, cascade-board
 */

interface JobBaySnapshot {
  allowed_bay_types_snapshot: string[] | null;
  required_capabilities_snapshot: string[] | null;
  excluded_bay_types_snapshot: string[] | null;
  resolved_bay_type?: string | null;
}

interface BayRequirements {
  allowedBays: string[];
  requiredCapabilities: string[];
  excludedBays: string[];
  hasRequirements: boolean;
}

/**
 * Compute allowed bay types for a ticket (INTERSECTION of all jobs' requirements)
 * 
 * Multi-job tickets can only be performed in bays where ALL jobs are compatible.
 * Example: Alignment + Wiper Blades requires alignment_rack (not flat).
 * 
 * @param jobs - Array of ticket_jobs with bay snapshot fields
 * @returns BayRequirements with intersection of all job requirements
 */
export function computeAllowedBaysForTicket(jobs: JobBaySnapshot[]): BayRequirements {
  // If no jobs or no bay requirements, allow any bay
  if (!jobs || jobs.length === 0) {
    return {
      allowedBays: [],
      requiredCapabilities: [],
      excludedBays: [],
      hasRequirements: false
    };
  }

  // Filter jobs that have bay requirements
  const jobsWithRequirements = jobs.filter(j => 
    (j.allowed_bay_types_snapshot && j.allowed_bay_types_snapshot.length > 0) ||
    (j.required_capabilities_snapshot && j.required_capabilities_snapshot.length > 0) ||
    (j.excluded_bay_types_snapshot && j.excluded_bay_types_snapshot.length > 0)
  );

  // If no jobs have requirements, allow any bay
  if (jobsWithRequirements.length === 0) {
    return {
      allowedBays: [],
      requiredCapabilities: [],
      excludedBays: [],
      hasRequirements: false
    };
  }

  // Step 1: INTERSECTION of allowed bay types
  let allowedBays: string[] = [];
  
  const jobsWithAllowedBays = jobsWithRequirements.filter(j => 
    j.allowed_bay_types_snapshot && j.allowed_bay_types_snapshot.length > 0
  );

  if (jobsWithAllowedBays.length > 0) {
    // Start with first job's allowed bays
    allowedBays = [...(jobsWithAllowedBays[0].allowed_bay_types_snapshot || [])];
    
    // Intersect with remaining jobs
    for (let i = 1; i < jobsWithAllowedBays.length; i++) {
      const jobAllowedBays = jobsWithAllowedBays[i].allowed_bay_types_snapshot || [];
      allowedBays = allowedBays.filter(bay => jobAllowedBays.includes(bay));
    }
  }

  // Step 2: UNION of required capabilities (must have ALL)
  const requiredCapabilities = Array.from(new Set(
    jobsWithRequirements.flatMap(j => j.required_capabilities_snapshot || [])
  ));

  // Step 3: UNION of excluded bay types
  const excludedBays = Array.from(new Set(
    jobsWithRequirements.flatMap(j => j.excluded_bay_types_snapshot || [])
  ));

  return {
    allowedBays,
    requiredCapabilities,
    excludedBays,
    hasRequirements: true
  };
}

/**
 * Check if a technician's bay assignments are compatible with ticket requirements
 * 
 * @param techBays - Technician's bay assignments (from shop_bays.bay_type)
 * @param requirements - Computed bay requirements for ticket
 * @returns true if technician can perform this ticket
 */
export function isTechBayCompatible(
  techBays: string[],
  requirements: BayRequirements
): boolean {
  // If no requirements, any tech is compatible
  if (!requirements.hasRequirements) {
    return true;
  }

  const { allowedBays, requiredCapabilities, excludedBays } = requirements;

  // Chronomancer R-BAY-1: multi-bay semantic — excluded bays must FILTER
  // out specific bays from the tech's ownership, not reject the whole
  // tech. A tech with [2-Post, Flat] on a job excluding Flat is still
  // compatible because they own a 2-Post. Previously this block rejected
  // any tech with a single excluded bay in their list, which drifted
  // from the server-side implementation at
  // supabase/functions/_shared/bayGatingValidator.ts:127-158.
  const normalizedTechBays = techBays.map(bay => normalizeBayType(bay));
  const nonExcludedBays = normalizedTechBays.filter(bay =>
    !excludedBays.includes(bay)
  );

  // If the tech's bays are entirely excluded, they are incompatible.
  if (nonExcludedBays.length === 0 && excludedBays.length > 0) {
    return false;
  }

  // Check allowed bays against the non-excluded subset only.
  if (allowedBays.length > 0) {
    const hasAllowedBay = nonExcludedBays.some(bay =>
      allowedBays.includes(bay)
    );
    if (!hasAllowedBay) {
      return false;
    }
  }

  // Check required capabilities using non-excluded bays only.
  if (requiredCapabilities.length > 0) {
    const nonExcludedRawBays = techBays.filter(bay =>
      !excludedBays.includes(normalizeBayType(bay))
    );
    const techCapabilities = getTechCapabilities(nonExcludedRawBays);
    const hasAllCapabilities = requiredCapabilities.every(cap =>
      techCapabilities.includes(cap)
    );
    if (!hasAllCapabilities) {
      return false;
    }
  }

  return true;
}

/**
 * Normalize bay types to canonical format (handles all variations)
 * Supports: display formats, legacy enum formats, and canonical formats
 */
function normalizeBayType(bayType: string): string {
  const normalizedInput = bayType.toLowerCase().trim();
  
  const mapping: Record<string, string> = {
    // Legacy enum formats
    '2-post': '2_post',
    '4-post': '4_post',
    'scissor': 'scissor_lift',
    'single-post': 'single_post',
    'alignment': 'alignment_rack',
    'alignment-adas': 'alignment_rack',
    'adas': 'adas_bay',
    'general': 'flat',
    
    // Display formats (from technician bay_assignments)
    '2-post lift': '2_post',
    '4-post lift': '4_post',
    'scissor lift': 'scissor_lift',
    'single-post lift': 'single_post',
    'alignment rack': 'alignment_rack',
    'alignment & adas': 'alignment_rack',
    'adas bay': 'adas_bay',
    'flat': 'flat',
    // Canonical formats (pass through)
    '2_post': '2_post',
    '4_post': '4_post',
    'scissor_lift': 'scissor_lift',
    'single_post': 'single_post',
    'alignment_rack': 'alignment_rack',
    'adas_bay': 'adas_bay',
  };
  
  return mapping[normalizedInput] || bayType;
}

/**
 * Get capabilities for a technician's bay assignments
 */
function getTechCapabilities(techBays: string[]): string[] {
  const capabilities = new Set<string>();
  
  techBays.forEach(bay => {
    const rawLower = bay.toLowerCase().trim();

    // SPECIAL CASE: alignment-adas bays have BOTH alignment AND ADAS capabilities.
    // Must check raw input BEFORE normalization loses the adas distinction.
    // Early return prevents fallthrough into normalized mapping (Guardrail #1).
    if (rawLower === 'alignment-adas' || rawLower === 'alignment & adas') {
      capabilities.add('has_lift');
      capabilities.add('supports_wheels_off_ground');
      capabilities.add('is_alignment');
      capabilities.add('is_adas');
      return; // forEach callback return = continue
    }

    const normalized = normalizeBayType(bay);
    
    // Map bay types to capabilities
    if (['2_post', '4_post', 'scissor_lift', 'alignment_rack', 'single_post'].includes(normalized)) {
      capabilities.add('has_lift');
    }
    if (['2_post', '4_post', 'scissor_lift', 'alignment_rack', 'single_post'].includes(normalized)) {
      capabilities.add('supports_wheels_off_ground');
    }
    if (['2_post', '4_post'].includes(normalized)) {
      capabilities.add('supports_engine_drop');
    }
    if (normalized === 'alignment_rack') {
      capabilities.add('is_alignment');
    }
    if (normalized === 'adas_bay') {
      capabilities.add('is_adas');
    }
  });
  
  return Array.from(capabilities);
}

/**
 * Format rejection reason for parking lot routing
 */
export function formatBayRejectionReason(
  requirements: BayRequirements,
  techName?: string
): string {
  const { allowedBays, requiredCapabilities, excludedBays } = requirements;
  
  const parts: string[] = [];
  
  if (allowedBays.length > 0) {
    parts.push(`Requires bay types: ${allowedBays.join(', ')}`);
  }
  
  if (requiredCapabilities.length > 0) {
    parts.push(`Requires capabilities: ${requiredCapabilities.join(', ')}`);
  }
  
  if (excludedBays.length > 0) {
    parts.push(`Excludes: ${excludedBays.join(', ')}`);
  }
  
  const techPart = techName ? ` Tech ${techName} not compatible.` : '';
  
  return `Bay requirements not met. ${parts.join('. ')}.${techPart}`;
}
